/**
 * The daily AI allowance: which day requests count toward, when that count
 * starts over, and what to tell people about it.
 *
 * Requests are counted per UTC day (server/ai/usage.ts). Anything shown to a
 * person says when that is in their own time zone, DST included.
 */
import type { AIUsage } from './api';

/** With this many requests left or fewer, AI actions say how many remain. */
export const LOW_AI_REQUESTS = 10;

/** The day AI requests count toward: the UTC calendar date. */
export function aiDay(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** When today's count starts over: the next midnight UTC. */
export function nextAIReset(now: Date = new Date()): Date {
  const next = new Date(now.getTime());
  next.setUTCHours(24, 0, 0, 0);
  return next;
}

/**
 * Usage as of `now`. Counts from before the reset don't hold after it (a tab that was
 * hidden or asleep at midnight UTC): until fresh numbers arrive, the new day starts at zero.
 */
export function currentAIUsage(usage: AIUsage, now: Date = new Date()): AIUsage {
  const resetsAt = Date.parse(usage.resetsAt);
  if (!Number.isFinite(resetsAt) || resetsAt > now.getTime()) return usage;
  return { ...usage, used: 0, serverLimitReached: false, resetsAt: nextAIReset(now).toISOString() };
}

const DAY_MS = 86_400_000;

/**
 * How long to wait before reloading the allowance at its reset (`jitterMs` spreads
 * everyone's reloads out). The reset is never more than a day away, so a longer wait
 * means this device's clock is off: it waits a day at most, and no setTimeout overflow.
 * A reset that passed here but not yet on the server asks again each minute for ten
 * minutes; after that it's left to the next focus or AI call.
 */
export function aiResetRefetchDelay(resetsAt: string | undefined, nowMs: number, jitterMs = 0): number | false {
  const at = Date.parse(resetsAt ?? '');
  if (!Number.isFinite(at)) return false;
  const wait = at - nowMs;
  if (wait > 0) return Math.min(wait, DAY_MS) + 2_000 + jitterMs;
  return wait > -10 * 60_000 ? 60_000 : false;
}

/**
 * ok: nothing to say. low: 10 or fewer left under a personal cap. out: none left.
 * busy: everyone's requests together hit the server-wide cap (AI_DAILY_LIMIT).
 */
export type AIAllowanceState = 'ok' | 'low' | 'out' | 'busy';

/** Requests left under the personal cap, never below 0. Null when only the server-wide cap applies. */
export function aiRequestsLeft(usage: Pick<AIUsage, 'used' | 'limit'>): number | null {
  return usage.limit === null ? null : Math.max(0, usage.limit - usage.used);
}

/** The personal cap comes first: it's the one the server checks (and names) first. */
export function aiAllowanceState(usage: Pick<AIUsage, 'used' | 'limit' | 'serverLimitReached'>): AIAllowanceState {
  const left = aiRequestsLeft(usage);
  if (left === 0) return 'out';
  if (usage.serverLimitReached) return 'busy';
  return left !== null && left <= LOW_AI_REQUESTS ? 'low' : 'ok';
}

interface ZoneFormats {
  clock: Intl.DateTimeFormat;
  date: Intl.DateTimeFormat;
}

// Formatters are slow to build and every AI note needs them. Null marks an unknown zone.
const zones = new Map<string, ZoneFormats | null>();

function formatsFor(timeZone: string): ZoneFormats | null {
  if (!zones.has(timeZone)) {
    if (zones.size >= 64) zones.clear();
    try {
      zones.set(timeZone, {
        clock: new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true }),
        // en-CA formats as YYYY-MM-DD.
        date: new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }),
      });
    } catch {
      zones.set(timeZone, null);
    }
  }
  return zones.get(timeZone) ?? null;
}

/** "7:00 PM" in a time zone (UTC if it's unknown). A no-break space keeps "PM" with the time when a line wraps. */
export function clockTime(at: Date, timeZone: string): string {
  const parts = (formatsFor(timeZone) ?? formatsFor('UTC')!).clock.formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('hour')}:${part('minute')}\u00a0${part('dayPeriod')}`;
}

/**
 * When the allowance comes back, in the person's time zone, to follow "They reset"
 * or "They come back": "at 7:00 PM" (later today), "tomorrow at 4:00 PM", or "at
 * midnight" where midnight UTC is local midnight. An unknown zone says "at midnight UTC".
 */
export function resetPhrase(resetsAt: Date | string, timeZone: string, now: Date = new Date()): string {
  let at = typeof resetsAt === 'string' ? new Date(resetsAt) : resetsAt;
  const formats = formatsFor(timeZone);
  if (!formats || !Number.isFinite(at.getTime())) return 'at midnight UTC';
  // A reset that has already passed (stale numbers) is followed by the next one.
  if (at.getTime() <= now.getTime()) at = nextAIReset(now);
  const time = clockTime(at, timeZone);
  if (time === '12:00\u00a0AM') return 'at midnight';
  return formats.date.format(at) === formats.date.format(now) ? `at ${time}` : `tomorrow at ${time}`;
}

/** For admins, who also manage the UTC day: "at 7:00 PM (midnight UTC)", or "at midnight UTC" where the two are the same. */
export function resetPhraseWithUTC(resetsAt: Date | string, timeZone: string, now: Date = new Date()): string {
  const phrase = resetPhrase(resetsAt, timeZone, now);
  return phrase === 'at midnight' || phrase === 'at midnight UTC' ? 'at midnight UTC' : `${phrase} (midnight UTC)`;
}

/**
 * One line for AI actions, or null when there's nothing to say. `ownServer` is a
 * copy without sign-in: the person reading runs it, so they can raise the cap.
 */
export function aiAllowanceMessage(state: AIAllowanceState, left: number | null, phrase: string, ownServer = false): string | null {
  switch (state) {
    case 'low':
      return `${left} AI ${left === 1 ? 'request' : 'requests'} left today`;
    case 'out':
      return `No AI requests left today. They come back ${phrase}.`;
    case 'busy':
      return ownServer
        ? `Today's AI limit is reached. It resets ${phrase}, or raise AI_DAILY_LIMIT in .env.`
        : `The AI is busy for today. It's back ${phrase}.`;
    default:
      return null;
  }
}
